import { KeyRound, Lock, ShieldCheck } from 'lucide-react'
import { useState } from 'react'
import { Link } from 'react-router'
import { toast } from 'sonner'
import { PageTitle } from '@/app/layout'
import { Avatar, Panel } from '@/components/redesign/bits'
import { Field } from '@/components/ui/field'
import { Input } from '@/components/ui/input'
import { useMe } from '@/features/auth/api'
import { useEmailMuted, useEmailPreference } from '@/features/notifications/api'
import { errorMessage, isApiError } from '@/lib/api/errors'
import type { Staff } from '@/lib/api/types'
import { useChangePassword, useUpdateName } from './api'

const roleNames = { owner: 'Owner', admin: 'Admin', support: 'Support' } as const

/**
 * Your own control panel account (redesign of 2026-10-16): name, notification emails, password
 * and two-step verification. The sign-in email can't be changed.
 */
export function ProfilePage() {
  const me = useMe()
  if (!me.data) return null
  const staff = me.data

  return (
    <>
      <PageTitle
        title="Your profile"
        description="Your name, password, two-step verification and whether notifications also come by email."
      />
      <div className="grid2">
        <NameCard staff={staff} />
        <EmailCard staff={staff} />
        <PasswordCard />
        <Panel
          title="Two-step verification"
          sub="Every sign-in asks for a code from your authenticator app."
          extra={
            <span className="chip ok">
              <ShieldCheck aria-hidden="true" className="size-3.5" />
              On
            </span>
          }
        >
          <p style={{ fontSize: 13.5, color: 'var(--muted)' }}>
            {staff.recovery_codes_left === 1
              ? '1 recovery code left.'
              : `${staff.recovery_codes_left} recovery codes left.`}{' '}
            Lost your phone? Sign in with a recovery code, or ask the owner to reset your two-step
            verification.
          </p>
        </Panel>
      </div>
    </>
  )
}

/** Notifications by email or only in the control panel, and which kinds (owner, 2026-10-20). */
function EmailCard({ staff }: { staff: Staff }) {
  const email = useEmailPreference()
  const mute = useEmailMuted()
  const on = staff.email_notifications
  const muted = staff.email_muted ?? []
  return (
    <Panel title="Email me about" sub={`Sent to ${staff.email}`}>
      <div className="srow" style={{ paddingTop: 0 }}>
        <div>
          <b style={{ fontWeight: 500 }}>Notifications</b>
          <small>
            New tickets and replies, payouts to send and service alerts, as on{' '}
            <Link to="/notifications">Notifications</Link>
          </small>
        </div>
        <button
          type="button"
          role="switch"
          className="switch"
          aria-checked={on}
          aria-label="Email me notifications"
          disabled={email.isPending}
          onClick={() =>
            email.mutate(!on, {
              onSuccess: (s) =>
                toast.success(
                  s.email_notifications
                    ? 'You’ll get these by email too.'
                    : 'Emails off. They still show here.',
                ),
              onError: (err) => toast.error(errorMessage(err)),
            })
          }
        />
      </div>
      {on &&
        EMAIL_KINDS.map(([category, label, help]) => {
          const wanted = !muted.includes(category)
          return (
            <div className="srow" key={category}>
              <div>
                <b style={{ fontWeight: 500 }}>{label}</b>
                <small>{help}</small>
              </div>
              <button
                type="button"
                role="switch"
                className="switch"
                aria-checked={wanted}
                aria-label={`Email me: ${label}`}
                disabled={mute.isPending}
                onClick={() =>
                  mute.mutate(wanted ? [...muted, category] : muted.filter((c) => c !== category), {
                    onSuccess: () =>
                      toast.success(
                        wanted ? `${label}: in the control panel only.` : `${label}: by email too.`,
                      ),
                    onError: (err) => toast.error(errorMessage(err)),
                  })
                }
              />
            </div>
          )
        })}
    </Panel>
  )
}

/** The kinds of notification, as the control panel groups them (StaffNotification categories). */
const EMAIL_KINDS: [string, string, string][] = [
  [
    'support',
    'Tickets and enquiries',
    'New tickets and website enquiries, replies, and tickets given to you',
  ],
  ['referrals', 'Referral payouts', 'Payouts to send to referrers'],
  ['feedback', 'Customer feedback', 'New ratings and messages from the app’s feedback'],
  ['system', 'Service alerts', 'Service limits and other alerts about how the service is running'],
]

function NameCard({ staff }: { staff: Staff }) {
  const { name, email } = staff
  const update = useUpdateName()
  const [value, setValue] = useState(name)
  const error = isApiError(update.error) ? update.error.errors.name?.[0] : undefined

  return (
    <Panel
      title="Your details"
      extra={
        <span className="who">
          <Avatar name={name} me />
          <small>{roleNames[staff.role]}</small>
        </span>
      }
    >
      <form
        noValidate
        className="form"
        onSubmit={(e) => {
          e.preventDefault()
          update.mutate(value, {
            onSuccess: () => toast.success('Name saved.'),
            onError: (err) => !isApiError(err) && toast.error(errorMessage(err)),
          })
        }}
      >
        <Field label="Name" htmlFor="profile-name" required error={error}>
          <Input
            id="profile-name"
            autoComplete="name"
            value={value}
            onChange={(e) => setValue(e.target.value)}
          />
        </Field>
        <Field
          label="Sign-in email"
          htmlFor="profile-email"
          hint="For security, your sign-in email can't be changed. To use a different one, ask the owner to invite it."
        >
          <div className="relative">
            <Input id="profile-email" value={email} readOnly disabled className="pr-9" />
            <Lock
              className="absolute top-1/2 right-3 size-4 -translate-y-1/2 text-ink-subtle"
              aria-hidden="true"
            />
          </div>
        </Field>
        <div className="bar">
          <button
            type="submit"
            className="btn primary push"
            disabled={update.isPending || value.trim() === name}
          >
            Save name
          </button>
        </div>
      </form>
    </Panel>
  )
}

function PasswordCard() {
  const change = useChangePassword()
  const empty = { current_password: '', password: '', password_confirmation: '' }
  const [form, setForm] = useState(empty)
  const errors = isApiError(change.error) ? change.error.errors : {}

  return (
    <Panel
      title="Password"
      sub="At least 12 characters. A short sentence you'll remember works well. We email you whenever it changes."
    >
      <form
        noValidate
        className="form"
        onSubmit={(e) => {
          e.preventDefault()
          change.mutate(form, {
            onSuccess: () => {
              setForm(empty)
              toast.success('Password changed.')
            },
            onError: (err) => !isApiError(err) && toast.error(errorMessage(err)),
          })
        }}
      >
        <Field label="Current password" htmlFor="pw-current" required error={errors.current_password?.[0]}>
          <Input
            id="pw-current"
            type="password"
            autoComplete="current-password"
            value={form.current_password}
            onChange={(e) => setForm({ ...form, current_password: e.target.value })}
          />
        </Field>
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="New password" htmlFor="pw-new" required error={errors.password?.[0]}>
            <Input
              id="pw-new"
              type="password"
              autoComplete="new-password"
              value={form.password}
              onChange={(e) => setForm({ ...form, password: e.target.value })}
            />
          </Field>
          <Field label="Repeat new password" htmlFor="pw-repeat" required>
            <Input
              id="pw-repeat"
              type="password"
              autoComplete="new-password"
              value={form.password_confirmation}
              onChange={(e) => setForm({ ...form, password_confirmation: e.target.value })}
            />
          </Field>
        </div>
        <div className="bar">
          <button
            type="submit"
            className="btn primary push"
            disabled={
              change.isPending || !form.current_password || !form.password || !form.password_confirmation
            }
          >
            <KeyRound aria-hidden="true" />
            Change password
          </button>
        </div>
      </form>
    </Panel>
  )
}
